import React, { useMemo } from 'react';
import { pipe, Option, Array as A } from 'effect';
import { generatePath, Outlet, useParams } from 'react-router-dom';

import LayoutCompany from '@/components/mui/Layouts/Company';
import CompanyNavigation from '@/pages/company/components/CompanyNavigation';
import StickySubheader from '@/components/mui/Layouts/components/StickySubheader';
import ROUTES_PATH from '@/router/routesPathConfig';
import Tabs, { TabFactory } from '@/components/Tabs';
import Container from '@mui/material/Container';
import { AccessInput } from '@/abilities/accessInput';
import { useCompanyDashboardAccess } from '@/abilities/companyDashboard/useCompanyDashboardAccess';
import { useCompanyDashboardDestinationsInAddress } from '@/abilities/companyDashboard/routes';
import { companyDashboardTabBar } from '@/pages/company/DashboardRoute/tabBar';
import { companyDashboardDestinationRoutes } from '@/pages/company/DashboardRoute/destinationRoutes';

const DashboardRoute: React.FC = () => {
  const { companyId = '' } = useParams();
  const destinations = useCompanyDashboardDestinationsInAddress();
  const tabInView = pipe(
    A.findFirst(companyDashboardTabBar, ({ destination }) => A.contains(destinations, destination)),
    Option.map(({ destination }) => destination),
    Option.getOrNull,
  );

  const access = useCompanyDashboardAccess();
  const tabs = useMemo(
    () =>
      AccessInput.$match(access, {
        Ready: ({ value }) =>
          companyDashboardTabBar
            .filter(({ destination }) => value.accessOf(destination) !== 'hidden')
            .map(({ destination, label }) =>
              TabFactory({
                ...label,
                to: generatePath(ROUTES_PATH[companyDashboardDestinationRoutes[destination]].absolute, { companyId }),
                tabValue: destination,
                betaBadge: true,
              }),
            ),
        Loading: () => [],
        Failed: () => [],
      }),
    [access, companyId],
  );

  return (
    <LayoutCompany suppressSearch defaultDrawerOpen={false} DrawerContent={<CompanyNavigation onDocMove={() => {}} />}>
      <StickySubheader
        AppBarProps={{ sx: { bgcolor: 'common.white', borderBottom: 1, borderColor: 'divider' } }}
        BoxProps={{ mb: 0 }}
      >
        <Tabs value={tabInView ?? false} tabs={tabs} />
      </StickySubheader>
      <Container maxWidth={false} sx={{ flexGrow: 1, py: 2, display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </Container>
    </LayoutCompany>
  );
};

export default DashboardRoute;
